import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';

import cannot from 'ember-can/helpers/cannot';
import { t } from 'ember-intl';
import { and, not, or } from 'ember-truth-helpers';

import Component from 'umg-ember-backend/components/record-detail/for/default';
import Mlp from 'umg-ember-backend/components/record-detail/forms/mlp';
import Toolbar from 'umg-ember-backend/components/record-detail/toolbar';
import Button from 'umg-ember-backend/components/ui/button';
import Window from 'umg-ember-backend/components/ui/window';
import recordHeadlineOrNew from 'umg-ember-backend/helpers/record-headline-or-new';
import recordOverline from 'umg-ember-backend/helpers/record-overline';
import { getRecordType } from 'umg-ember-backend/utils/get-record-type';

export default class RecordDetailForPageBuilderComponent extends Component {
  // Services
  @service fetch;
  @service recordStatus;

  // Getter and setter
  get isDisabled() {
    return this.recordStatus.isDisabled(this.args.model);
  }

  // Functions
  saveRecord = (model) => {
    return this._saveMlp(model);
  };

  saveAndUnloadRecord = async (model) => {
    await this._saveMlp(model);

    this.transitionToPrevBreadcrumb();
  };

  publishRecord = async (model) => {
    let ok = await this.history.confirmAction('publish', model);

    if (!ok) {
      return;
    }

    const publishedModel = model;

    try {
      this.recordStatus.setPublishing(publishedModel, true);
      model.cmsState = 2;

      model = await this.history.publishModel(model);

      window.open(model.liveUrl, `mlp_live_${model.id}`);
    } catch (error) {
      this.flashMessages.serverError(error);
    } finally {
      this.recordStatus.setPublishing(publishedModel, false);
    }
  };

  unpublishRecord = async (model) => {
    let ok = await this.history.confirmAction('unpublish', model);

    if (!ok) {
      return;
    }
    const unpublishedModel = model;

    try {
      this.recordStatus.setPublishing(unpublishedModel, true);
      model.cmsState = 1;

      await this.history.publishModel(model);
    } catch (error) {
      this.flashMessages.serverError(error);
    } finally {
      this.recordStatus.setPublishing(unpublishedModel, false);
    }
  };

  reset = async (model) => {
    let ok = await this.history.confirmAction('reset', model);

    if (!ok) {
      return;
    }

    try {
      let resetedModel = await this.history.resetMlp(model);
      window.open(resetedModel.previewUrl, `mlp_preview_${resetedModel.id}`);
    } catch (error) {
      this.flashMessages.serverError(error);
    }
  };

  preview = async (model) => {
    await this.saveRecord(model);
    model.preview();
  };

  deleteCache = async (model) => {
    try {
      await this.fetch.delete(`/mlps/purge/${model.id}`);
      this.flashMessages.success('record_detail.for.mlp.delete_cache_success');
    } catch (error) {
      this.flashMessages.serverError(error);
    }
  };

  _saveMlp = async (model) => {
    // If the validation fails model is null
    model = await this.history.saveModel(model);
    if (!model) {
      return;
    }

    let pages = model.get('pages');

    // TODO: REFACTOR-MLP-API Get rid of it. We need to remove the new content. After createing a new content item, the content item from the response is not mapped with the new page in ember-data. https://github.com/emberjs/data/issues/1829
    pages.forEach((page) => {
      let contentItems = page.get('contentItems');
      let newContentItems = this.findNewContentItems(contentItems);
      let entities = newContentItems.map((item) => item.entity).filter(Boolean);

      if (entities.length) {
        entities.forEach((item) => {
          this.recordRelationships.rollback(item);

          // ember-cp-validations initializes lazily. Materialize validations
          // before rollback unloads the new record so its destroy hook cannot
          // re-enter the record teardown.
          void item.validations;

          item.rollbackAttributes();
        });
      }

      newContentItems.forEach((item) => {
        this.recordRelationships.rollback(item);
        item.rollbackAttributes();
      });
    });

    // TODO: REFACTOR-MLP-API Get rid of it. We need to remove the new pages. After createing a new page, the page from the response is not mapped with the new page in ember-data. https://github.com/emberjs/data/issues/1829
    let newPages = pages.filter((page) => page.get('isNew'));
    newPages.forEach((item) => {
      item.rollbackAttributes();
      this.recordRelationships.rollback(item);
    });

    // We return the model save promise to handle things after the mlp is saved.
    return model;
  };

  findNewContentItems(contentItems) {
    return contentItems.reduce((accumulator, contentItem) => {
      let children = contentItem.get('children');
      let newContentItems = this.findNewContentItems(children);

      if (contentItem.get('isNew')) {
        newContentItems.push(contentItem);
      }

      return accumulator.concat(newContentItems);
    }, []);
  }

  // Template
  <template>
    {{#if (and @model (not @model.isDeleted))}}
      <Window
        @onBodyScroll={{fn (mut this.scrollPosition)}}
        @scrollPosition={{this.scrollPosition}}
        @isLoading={{@model.isLoading}}
        data-test-record-detail='mlp'
      >
        <:overline>
          {{recordOverline @model}}
        </:overline>

        <:title>
          {{recordHeadlineOrNew @model}}
        </:title>

        <:toolbar>
          <Toolbar
            @isCompact={{this.isScrolled}}
            @model={{@model}}
            @primaryActions={{hash
              onCancel=this.transitionToPrevBreadcrumb
              onPublish=(fn this.publishRecord @model)
              onSave=(fn this.saveRecord @model)
              onSaveAndUnload=(fn this.saveAndUnloadRecord @model)
              onPreview=(fn this.preview @model)
            }}
            @secondaryActions={{hash
              onDeleteRecord=(fn this.deleteRecordInDetail @model)
              onForceUnlock=(fn this.forceUnlockRecord @model)
              onReset=(fn this.reset @model)
              onUnblock=(fn this.unblockRecord @model)
              onUnpublish=(fn this.unpublishRecord @model)
              onBlock=(fn this.blockRecord @model)
            }}
          >
            <:additonalPrimaryActions>
              {{#if @model.isV2}}
                <Button
                  @style='outline'
                  @label={{t 'record_detail.for.mlp.delete_cache'}}
                  @icon='block'
                  disabled={{or (cannot 'update' @model) this.isDisabled}}
                  {{on 'click' (fn this.deleteCache @model)}}
                />
              {{/if}}
            </:additonalPrimaryActions>
          </Toolbar>
        </:toolbar>

        <:body>
          <Mlp
            @model={{@model}}
            @recordType={{getRecordType @model}}
            @onPublishRecord={{this.publishRecord}}
            @onSaveRecord={{fn this.saveRecord @model}}
            @showErrors={{this.history.showErrors}}
          />
        </:body>
      </Window>
    {{/if}}
  </template>
}
